import { useInfiniteQuery } from '@tanstack/react-query';
import { type FC } from 'react';

import Icon from '@/components/Icon';
import Text from '@/components/Text';
import { useExternalCandidate } from '@/hooks/useExternalCandidate';
import { useFetchGithubData } from '@/hooks/useFetchGithubData';
import { queries } from '@/queries';

import GithubData from './GithubData';
import GithubUrlField from './GithubUrlField';
import UpdateGithubDataButton from './UpdateGithubDataButton';

type Props = {
  id: number;
};

const Github: FC<Props> = ({ id }) => {
  const { mutate } = useFetchGithubData(id);

  const {
    data,
    isLoading: isProfileDataLoading,
    isError,
  } = useExternalCandidate(id, {
    staleTime: 1000 * 60 * 10,
    refetchInterval: (queryData) =>
      queryData?.githubProfile?.status === 'PROCESSING' ? 1000 * 5 : false,
  });

  const repoQueryEnabled = !!data?.githubProfile;

  const {
    data: repoData,
    isInitialLoading: isRepoDataInitialLoading,
    isLoading: isRepoDataLoading,
    isFetching: isRepoDataFetching,
    hasNextPage,
    fetchNextPage,
    isError: isRepoDataError,
    refetch,
  } = useInfiniteQuery({
    ...queries.externalCandidates.githubRepositories(id),
    staleTime: 1000 * 60 * 10,
    enabled: repoQueryEnabled,
    getNextPageParam: ({ count }, pages) =>
      pages.flatMap(({ results: pageData }) => pageData).length < count
        ? pages.length
        : undefined,
    refetchInterval: (queryData) =>
      queryData?.pages?.[0].status === 'PROCESSING' ? 1000 * 5 : false,
  });

  const isLoading =
    isProfileDataLoading ||
    isRepoDataInitialLoading ||
    (repoQueryEnabled && isRepoDataLoading) ||
    data?.githubProfile?.status === 'PROCESSING';

  const flattenRepoData = repoData?.pages.flatMap((page) => page.results) ?? [];

  return (
    <>
      <div className="flex w-full items-center gap-2 border-b border-neutral-800 pb-2">
        <Icon icon="mdi:github" fontSize={20} />
        <Text className="font-semibold">GitHub</Text>
      </div>
      <div className="flex flex-1 flex-col gap-5">
        <div className="flex gap-4">
          <GithubUrlField profileId={id} />
          <UpdateGithubDataButton isLoading={isLoading} profileId={id} />
        </div>
        <GithubData
          data={data?.githubProfile ?? null}
          isLoading={isLoading}
          repoData={flattenRepoData}
          loadMoreRepos={hasNextPage ? fetchNextPage : undefined}
          moreReposLoading={isRepoDataFetching}
          isError={
            data?.githubProfile?.status === 'FAILED' ||
            isError ||
            isRepoDataError
          }
          error={data?.githubProfile?.errorType}
          onRetry={data?.githubProfile?.status === 'FAILED' ? mutate : refetch}
          currentDataCount={flattenRepoData.length}
          dataCount={repoData?.pages?.[0]?.count ?? 0}
        />
      </div>
    </>
  );
};

export default Github;
